import Text from 'components/shared/Text'
import Section from 'components/shared/Section'
import { FormattedMessage } from 'react-intl'
import Link from 'components/shared/Link'
import Box from 'components/shared/Box'
import PersonType from './PersonType'
import ZoneType from './ZoneType'
import TicketType from './TicketType'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import { removeTicket } from 'store/ticket-slice/index'
import { useEffect, useState } from 'react'
import { useTickets } from 'store/ticket-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import Loader from 'components/shared/Loader'
import { DefaultTheme } from 'styled-components'

interface TicketConfigurationProps {
    index: number
    boxId: string
    theme?: DefaultTheme
}

const TicketConfiguration = ({
    index,
    boxId,
    theme,
}: TicketConfigurationProps) => {
    const { calculateScroll } = useAutoScroll()
    const dispatch = useAppDispatch()
    const { ticketBoxes } = useAppSelector((state) => state.ticket)
    const { tickets: cartTickets, ticketsBundles } = useAppSelector(
        (state) => state.cart.items
    )
    const ticket = ticketBoxes.find((box) => box.boxId === boxId)
    const { person, zone } = ticket || {}
    const { removeTicketFromCart, isTicketInCart } = useTickets(boxId)
    const { refetchCart, isCartFetching } = useCart()

    const [ticketToRemove, setTicketToRemove] = useState<string | null>(null)

    const shouldShowDeleteBtn =
        (cartTickets.length !== 0 && ticketBoxes.length !== 1) ||
        !!ticketsBundles.length
    const sectionId = `tickets-${index}`

    const removeTicketSection = (boxId: string) => {
        dispatch(removeTicket(boxId))

        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: 'delete',
            index,
        })
    }

    const handleRemoveTicket = () => {
        setTicketToRemove(boxId)

        if (isTicketInCart) {
            removeTicketFromCart()
        } else {
            refetchCart()
        }
    }

    useEffect(() => {
        if (isCartFetching || !ticketToRemove) return
        removeTicketSection(ticketToRemove)
        setTicketToRemove(null)
    }, [isCartFetching])

    return (
        <Section id={sectionId} theme={theme}>
            <Box
                direction="row"
                align="center"
                justify="space-between"
                mb={[32, 40]}
            >
                <Text theme={theme} variant="heading2" color="grayscale_900">
                    <FormattedMessage
                        id="tickets.ticket_label"
                        defaultMessage="Bilet"
                    />{' '}
                    {index + 1}
                </Text>

                {ticketToRemove ? (
                    <Loader />
                ) : (
                    shouldShowDeleteBtn && (
                        <Link
                            variant="secondary"
                            icon="bin"
                            onClick={() => handleRemoveTicket()}
                            theme={theme}
                            iconColor={
                                theme?.mode === 'dark'
                                    ? 'primary_500'
                                    : undefined
                            }
                            data-testid={`ticket-${index}-button-delete`}
                        >
                            <FormattedMessage
                                id="global.btn.remove"
                                defaultMessage="Usuń"
                            />
                        </Link>
                    )
                )}
            </Box>
            <Box gap={[32, 40]}>
                <PersonType index={index} boxId={boxId} theme={theme} />
                {person && (
                    <ZoneType
                        index={index}
                        person={person}
                        boxId={boxId}
                        theme={theme}
                    />
                )}
                {person && zone && (
                    <TicketType
                        index={index}
                        person={person}
                        zone={zone}
                        boxId={boxId}
                        theme={theme}
                    />
                )}
            </Box>
        </Section>
    )
}

export default TicketConfiguration
